import React, { useEffect, useRef } from 'react';
import { UnstyledButton } from '@mantine/core';
import classes from './pos-wheel.module.scss';

export type PosWheelItem = { key: string; label: string; selected: boolean; onClick: () => void; testid?: string };

export type PosWheelColumnProps = { items: PosWheelItem[]; disabled?: boolean };

// One scroll-snap wheel column with a centered selected pill; click a cell to select it.
export const PosWheelColumn: React.FC<PosWheelColumnProps> = ({ items, disabled }) => {
  const columnRef = useRef<HTMLDivElement>(null);
  const selectedKey = items.find((item) => item.selected)?.key;

  // Center the selected cell under the pill on mount + when the selection changes (drum feel).
  useEffect(() => {
    const col = columnRef.current;
    const sel = col?.querySelector<HTMLElement>('[data-wheel-selected="true"]');
    if (col && sel) {
      col.scrollTop = sel.offsetTop - col.clientHeight / 2 + sel.clientHeight / 2;
    }
  }, [selectedKey]);

  return (
    <div ref={columnRef} className={classes.column}>
      <div className={classes.columnInner}>
        {items.map((item) => (
          <UnstyledButton
            key={item.key}
            component="button"
            type="button"
            disabled={disabled}
            data-wheel-selected={item.selected || undefined}
            data-testid={item.testid}
            className={item.selected ? `${classes.cell} ${classes.cellSelected}` : classes.cell}
            onClick={item.onClick}
          >
            {item.label}
          </UnstyledButton>
        ))}
      </div>
    </div>
  );
};

export type PosWheelProps = { children: React.ReactNode; dir?: 'ltr' | 'rtl' };

// Drum container: centered pill behind N PosWheelColumns; dir sets column order (ltr/rtl).
export const PosWheel: React.FC<PosWheelProps> = ({ children, dir = 'rtl' }) => (
  <div className={classes.wheels} dir={dir}>
    <div className={classes.selectionPill} aria-hidden="true" />
    {children}
  </div>
);
